import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart, Bar, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageContainer from '../common/PageContainer';
import PageHeader from '../common/PageHeader';
import axiosInstance from '../../api/axiosConfig';

const normalizeGenderDistribution = (raw, totals) => {
  if (raw && Object.keys(raw).length > 0) {
    return Object.entries(raw).map(([label, value]) => ({
      label: label || 'Unknown',
      value: Number(value || 0),
    }));
  }
  const fallback = [
    { label: 'Boys', value: Number(totals?.boys || 0) },
    { label: 'Girls', value: Number(totals?.girls || 0) },
    { label: 'Others', value: Number(totals?.others || 0) },
  ];
  return fallback.filter((row) => row.value > 0);
};

const StudentsAnalytics = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/dashboard/overview');
      setOverview(response?.data?.data || null);
    } catch (err) {
      setOverview(null);
      setError(err?.response?.data?.message || 'Failed to load student analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const totals = overview?.summary?.totals || {};
  const analytics = overview?.analytics || {};
  const trends = overview?.trends || {};

  const genderData = useMemo(
    () => normalizeGenderDistribution(trends?.genderDistribution || totals?.studentGenderDistribution, totals),
    [totals, trends]
  );

  const attendanceTrend = Array.isArray(trends?.studentAttendanceTrend) ? trends.studentAttendanceTrend : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Students Analytics"
        subtitle="Snapshot of student strength, attendance, and demographic trends."
        actions={
          <Button variant="contained" onClick={() => navigate('/students/list')}>
            View Student List
          </Button>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Total Students
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {Number(totals?.students || 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Student Attendance Rate
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {Number.isFinite(analytics?.studentAttendanceRate)
                ? `${Number(analytics.studentAttendanceRate).toFixed(1)}%`
                : 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Student Teacher Ratio
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {Number.isFinite(analytics?.studentTeacherRatio) ? analytics.studentTeacherRatio : 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Hostellers
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {Number(totals?.hostlers || 0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Gender Distribution
            </Typography>
            {genderData.length ? (
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={genderData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No gender distribution data available.
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Student Attendance Trend
            </Typography>
            {attendanceTrend.length ? (
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={attendanceTrend} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="rate" stroke={theme.palette.success.main} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Attendance trend data not available.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Next Steps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the student list to view profiles, manage admissions, and take action on individual records.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/students/list')}>
            Go to Student List
          </Button>
        </Stack>
      </Paper>
    </PageContainer>
  );
};

export default StudentsAnalytics;
